import { Controller } from "@hotwired/stimulus"

// DrawerLayoutComponent — the generic body-plus-drawer layout (MC-825).
//
// Non-modal by design: unlike `core--drawer` (the off-canvas modal overlay)
// this controller owns only open/close of the drawer region and add / remove
// of a trailing panel. No focus-trap, no backdrop, no ESC-dismiss, no scroll
// lock — the body stays visible and interactive beside the drawer.
//
// All state is DOM-derived, never held in module- or closure-level state:
//   - The open/closed flag lives in `data-state` on the root element.
//   - The panels are the live `<turbo-frame>` children of the panels target.
// So a Turbo Drive navigation or a `<turbo-frame>` swap that repaints the DOM
// leaves the controller's view of the world intact, and:
//   - connect() is idempotent — it defaults `data-state` if unset and
//     reconciles the drawer's visibility to it (correcting a Turbo-cache
//     snapshot painted before the network response lands). Re-running it is
//     a no-op.
//   - disconnect() has nothing to tear down: connect() binds no document- or
//     window-level listeners and stores no timers, so there is nothing to
//     leak across a navigation. It is defined for symmetry and to keep the
//     teardown contract explicit.
export default class extends Controller {
  static targets = ["drawer", "panels"]

  connect() {
    if (!this.element.dataset.state) {
      this.element.dataset.state = "closed"
    }
    // Sync the drawer's `hidden` attribute to the (possibly cache-restored)
    // state, so the body's push-layout matches what `data-state` claims.
    this.reconcile()
  }

  disconnect() {
    // No external listeners or timers were registered — nothing to release.
  }

  open(event) {
    event?.preventDefault()
    this.setOpen(true)
  }

  close(event) {
    event?.preventDefault()
    // MC-827: closing the drawer clears the added panels so reopening it
    // (a card click) never resurrects a stale trailing panel — the drawer
    // returns to its single base panel.
    this.clearTrailingPanels()
    this.setOpen(false)
  }

  toggle(event) {
    event?.preventDefault()
    this.setOpen(!this.isOpen())
  }

  // Point an existing panel at a path and open the drawer. Unlike
  // `addPanel`, this reuses a panel already in the DOM rather than
  // stacking a new one — the shape a single-panel consumer (the board's
  // task drawer) wants: each trigger retargets the one panel. The frame
  // to retarget is named by `data-drawer-layout-frame-param`
  // (`event.params.frame`); with none, the first panel is used. The path
  // comes from `data-drawer-layout-src-param` (`event.params.src`).
  // Setting `src` lets Turbo fetch and swap the frame's content in place.
  openPanel(event) {
    event?.preventDefault()
    if (!this.hasPanelsTarget) return

    const src = event?.params?.src
    const frameId = event?.params?.frame
    const panel = frameId
      ? this.panelsTarget.querySelector(`#${CSS.escape(frameId)}`)
      : this.panelsTarget.firstElementChild
    if (panel && src) panel.setAttribute("src", src)

    this.setOpen(true)
  }

  // Append a trailing panel — a fresh `<turbo-frame>` pointed at a path.
  // The path comes from `data-drawer-layout-src-param` on the action's
  // element (Stimulus surfaces it as `event.params.src`). Opens the drawer
  // if it was closed. The panel count is not capped.
  //
  // Idempotent by id: a stable `data-drawer-layout-frame-param`
  // (`event.params.frame`) names "the single trailing panel of this kind".
  // If a panel with that id is already open, retarget its `src` in place
  // rather than stacking a second copy — the browser shape (task + one
  // leases panel, never three, MC-827). Without a `frame` param every call
  // appends a fresh auto-id panel (the uncapped-stack shape).
  addPanel(event) {
    event?.preventDefault()
    if (!this.hasPanelsTarget) return

    const src = event?.params?.src
    const frameId = event?.params?.frame

    const existing = frameId
      ? this.panelsTarget.querySelector(`#${CSS.escape(frameId)}`)
      : null
    if (existing) {
      if (src) existing.setAttribute("src", src)
      this.setOpen(true)
      return
    }

    const frame = document.createElement("turbo-frame")
    frame.id = frameId || this.nextPanelId()
    frame.className =
      "component drawer-layout__panel flex h-full w-96 flex-none flex-col " +
      "overflow-y-auto border-l border-border first:border-l-0"
    if (src) frame.setAttribute("src", src)

    this.panelsTarget.appendChild(frame)
    this.setOpen(true)
  }

  // Remove the last panel. When the last one goes, close the drawer so the
  // body returns to full width.
  removeTrailingPanel(event) {
    event?.preventDefault()
    if (!this.hasPanelsTarget) return

    const panels = this.panelsTarget.children
    if (panels.length === 0) return

    panels[panels.length - 1].remove()
    if (this.panelsTarget.children.length === 0) {
      this.setOpen(false)
    }
  }

  // MC-827: drop every panel after the first (the server-rendered base
  // panel), returning the drawer to its single base panel. Called on
  // `close` so a reopened drawer never shows a trailing panel added in a
  // prior open. A drawer with only its base panel is left untouched.
  clearTrailingPanels() {
    if (!this.hasPanelsTarget) return
    while (this.panelsTarget.children.length > 1) {
      this.panelsTarget.lastElementChild.remove()
    }
  }

  // --- state -------------------------------------------------------------

  isOpen() {
    return this.element.dataset.state === "open"
  }

  setOpen(open) {
    this.element.dataset.state = open ? "open" : "closed"
    this.reconcile()
    // Generic open/close lifecycle event so a consumer can react to the drawer
    // opening or closing without this controller knowing who is listening — it
    // stays content-agnostic (no board knowledge). The board's card-selection
    // controller (MC-834) listens for these to re-mark the selected card.
    // Dispatched on every setOpen call (not just on state change) so a card
    // click that retargets the already-open panel still notifies listeners.
    // Bubbles to ancestors by default.
    this.dispatch(open ? "open" : "close")
  }

  // Drive the drawer's visibility (and thus the body's width) purely from
  // `data-state`. Hidden via the `hidden` attribute so a closed drawer takes
  // no layout space and the `flex-1` body spans full width.
  reconcile() {
    if (this.hasDrawerTarget) {
      this.drawerTarget.hidden = !this.isOpen()
    }
  }

  // A DOM-unique id for a runtime-appended frame. Derived from a per-connect
  // sequence plus the current panel count — no module-level state, so it
  // survives navigations without collisions within a session.
  nextPanelId() {
    this.panelSeq = (this.panelSeq || 0) + 1
    return `drawer-layout-panel-${this.panelsTarget.children.length}-${this.panelSeq}`
  }
}
